import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { HelperText, TextInput } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import FieldError from 'src/presentation/components/atoms/FieldError';
import { PrimaryButton } from 'src/presentation/components/molecules/Button';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import SelectField from 'src/presentation/components/molecules/SelectField';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useProfileStore } from 'src/domain/stores/useProfileStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Styles from 'src/core/utils/Styles';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import type { AccountNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import Translations from 'src/core/utils/Translations';

export default () => {
	const navigation = useNavigation<StackNavigationProp<AccountNavigation>>();
	const tabBarInset = useTabBarInset();
	const { user } = useAuthStore();
	const {
		profile: storedProfile,
		draft,
		isLoading,
		isSaving,
		validationErrors,
		saveErrorMessage,
		getApiProfile,
		putApiAddress,
		setAddressField,
		discardDraft,
	} = useProfileStore();
	const { logEvent } = useServicesStore();

	const profile = draft ?? storedProfile;

	useAppStateScreenListener({
		onInit: () => getApiProfile(),
		onUnfocussed: () => discardDraft(),
	});

	const addressFields = profile?.address_data.fields;

	const countryOptions = Object.entries(addressFields?.country.options ?? {}).map(([key, label]) => ({
		label,
		value: key,
	}));

	const putProfileData = async () => {
		if (!addressFields) {
			return;
		}

		const extractedData: Record<string, string | null> = {};
		Object.entries(addressFields).forEach(([key, field]) => {
			extractedData[key] = field.value;
		});

		logEvent({
			name: Constants.analyticsEventChangeAccountSettings,
			params: {
				[Constants.analyticsParamScreenPage]: Constants.screenEditProfile,
				[Constants.analyticsParamSelectItem]: Constants.analyticsValueSelectItemAddressData,
			},
			user,
		});

		await putApiAddress({
			data: extractedData,
			onSuccess: (data) => {
				if (data) {
					navigation.goBack();
				}
			},
		});
	};

	const handleChangeValue = (fieldKey: string, newValue: string) => {
		setAddressField({ key: fieldKey, value: newValue });
	};

	const fieldErrors = (fieldKey: string): string[] | undefined => validationErrors[fieldKey];

	return (
		<View style={Styles.screen.white}>
			{isLoading || isSaving ? (
				<LoadingIndicator style={{ flex: Dimens.flex1 }} />
			) : (
				<ScrollView contentContainerStyle={{ paddingBottom: tabBarInset }}>
					<View style={[styles.settingBlock]}>
						<View style={Styles.screen.column}>
							<Text ellipsizeMode="head" numberOfLines={1} style={styles.sectionHeading}>
								{profile?.address_data.label}
							</Text>
							{addressFields &&
								Object.entries(addressFields).map(([key, field]) => {
									switch (field.type) {
										case Constants.profileFieldTypeSelect:
											return (
												<View key={key}>
													<SelectField
														items={countryOptions}
														key={key + field.type}
														value={field.value ?? ''}
														onValueChange={(itemValue) => handleChangeValue(key, itemValue)}
													/>
													<FieldError message={fieldErrors(key)} />
												</View>
											);
										default:
											return (
												<View key={key}>
													<TextInput
														key={key + field.type}
														label={field.label}
														style={styles.textInput}
														value={field.value ?? ''}
														onChangeText={(e) => {
															handleChangeValue(key, e);
														}}
													/>
													<FieldError message={fieldErrors(key)} />
												</View>
											);
									}
								})}
							<HelperText type="error" visible={saveErrorMessage !== null}>
								{saveErrorMessage}
							</HelperText>
							<PrimaryButton
								extraStyle={{ marginTop: Dimens.padding25 }}
								label={Translations.save}
								onPress={() => putProfileData()}
							/>
						</View>
					</View>
				</ScrollView>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	settingBlock: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingVertical: Dimens.padding24,
		marginHorizontal: Dimens.padding18,
	},
	sectionHeading: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize23,
		lineHeight: Dimens.lineHeight30,
		color: Colors.gray800,
		paddingTop: Dimens.padding10,
		paddingBottom: Dimens.padding20,
	},
	textInput: {
		backgroundColor: Colors.gray100,
		marginTop: Dimens.padding8,
	},
});
